import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import DashboardTutorialEngine from "../../components/dashboard/DashboardTutorialEngine";
import OnboardingTelemetryBridge from "../../components/dashboard/OnboardingTelemetryBridge";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { settingsApi } from "../../api/settings";
import { activityApi } from "../../api/activity";
import { formatApiError, getApiBaseUrl } from "../../api/client";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { productsApi } from "../../api/products";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import { effectiveProjectCapabilities, isProjectCapabilityEnabled } from "../../utils/projectCapabilities";
import {
  buildProjectSecurePreviewUrl,
  getProjectPublicUrl,
} from "../../utils/projectDeployment";
import { assetValueFromResponse, isManagedLandingAsset } from "../../utils/landingAssets";
import { TRIAL_LANDING_EVENT } from "../../utils/trialOnboarding";
import LandingSettingsDialog from "../../components/dashboard/landingEditor/LandingSettingsDialog";
import {
  emptySettings,
  mergeSettings,
} from "../../components/dashboard/landingEditor/config";
import {
  findStepForSection,
  getSiteBuilderProgress,
} from "../../components/dashboard/landingEditor/siteBuilderModel";
import LandingStudioToolbar from "../../components/dashboard/landingStudio/LandingStudioToolbar";
import LandingEditorIntroCoach from "../../components/dashboard/landingStudio/LandingEditorIntroCoach";
import LandingStudioQuestionDock from "../../components/dashboard/landingStudio/LandingStudioQuestionDock";
import LandingStudioCanvasStage from "../../components/dashboard/landingStudio/LandingStudioCanvasStage";
import { inspectGastronomicLandingPreset } from "../../components/dashboard/landingStudio/gastronomicLandingPreset";
import LandingCanvas from "../../components/dashboard/landingEditor/LandingCanvas";
import { withExperienceDefaultOverrides } from "../../components/dashboard/landingEditor/siteExperienceDefaults";
import {
  getLandingSegments,
  isCanonicalLandingSegment,
  isStructuralLandingType,
  removeLandingSegment,
} from "../../components/dashboard/landingEditor/landingLayout";
import LandingSegmentInspector from "../../components/dashboard/landingStudio/LandingSegmentInspector";
import LandingElementInspector from "../../components/dashboard/landingStudio/LandingElementInspector";
import LandingRevisionsDrawer from "../../components/dashboard/landingStudio/LandingRevisionsDrawer";
import LandingAccessibilityDrawer from "../../components/dashboard/landingStudio/LandingAccessibilityDrawer";
import ProductsPageEditor from "../../components/dashboard/landingStudio/pageEditors/ProductsPageEditor";
import ProductPageEditor from "../../components/dashboard/landingStudio/pageEditors/ProductPageEditor";
import MegaMenuEditor from "../../components/dashboard/landingStudio/pageEditors/MegaMenuEditor";
import WebPushPromptEditor from "../../components/dashboard/landingStudio/pageEditors/WebPushPromptEditor";
import ContactPageEditor, { ContactPageDraftPreview } from "../../components/dashboard/landingStudio/pageEditors/ContactPageEditor";
import { buildLandingAccessibilityAudit } from "../../components/dashboard/landingStudio/landingAccessibilityAudit";
import { getLandingPreviewDevice } from "../../components/dashboard/landingStudio/previewDevices";
import { resolveNextStudioQuestion } from "../../components/dashboard/landingStudio/landingStudioQuestions";
import {
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
} from "../../components/dashboard/landingStudio/segmentRegistry";
import {
  getElementSectionContext,
  resolveEditableElementSelection,
} from "../../components/dashboard/landingStudio/elementEditing";
import {
  copySectionIdForPath,
  validateLandingCopyDraft,
} from "../../components/dashboard/landingStudio/copyLimits";
import {
  setManyValuesAtPaths,
  setValueAtPath,
} from "../../components/dashboard/landingStudio/landingStudioUtils";

import {
  draftSettingsPayload,
  deploymentFromPublishResponse,
  deploymentJobFromPublishResponse,
  deploymentJobId,
  isPendingDeploymentJob,
  deploymentJobFailure,
  openPreviewPlaceholder,
  collectManagedAssets,
  sameEditorValue,
  preserveVersionMetadata
} from "../../components/dashboard/landingStudio/editor/landingEditorPageUtils";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
let pendingEditorHistoryExit = null;

function getHashRoutePath(hashValue) {
  const rawHash = String(hashValue || "").replace(/^#/, "");
  const routeValue = rawHash || "/";
  const routePath = routeValue.split("?")[0].split("#")[0] || "/";
  return routePath.length > 1 ? routePath.replace(/\/+$/, "") : routePath;
}

function isCurrentEditorHash(hashValue, projectId) {
  const match = getHashRoutePath(hashValue).match(/^\/editor-web\/([^/]+)$/);
  if (!match) return false;
  try {
    return decodeURIComponent(match[1]) === String(projectId || "");
  } catch (_error) {
    return match[1] === String(projectId || "");
  }
}

function isEditableKeyboardTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  if (target.closest("input, textarea, select, [role='textbox']")) return true;
  return Boolean(target.closest('[contenteditable]:not([contenteditable="false"])'));
}

function sessionHistoryLabel(groupKey = "") {
  const key = String(groupKey || "").toLowerCase();
  if (key === "settings-dialog") return "Ajustes de la página";
  if (key.startsWith("asset:")) return "Imagen o recurso";
  if (key.includes("navigation")) return "Navegación";
  if (key.includes("hero")) return "Portada";
  if (key.includes("highlight")) return "Diferenciales";
  if (key.includes("testimonial")) return "Testimonios";
  if (key.includes("faq")) return "Preguntas frecuentes";
  if (key.includes("product")) return "Productos";
  if (key.includes("footer")) return "Pie de página";
  if (key.includes("business")) return "Datos del negocio";
  if (key.includes("theme") || key.includes("design")) return "Marca y estilos";
  if (key.includes("contact")) return "Contacto";
  if (key.includes("gastronomic-preset")) return "Adaptación para Carta";
  return "Cambio de la página";
}

export default function ProjectLandingVisualEditorPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const publicationIntent = React.useMemo(() => {
    const action = new URLSearchParams(location.search).get("action");
    return action === "publish" ? `${projectId}:publish` : "";
  }, [location.search, projectId]);
  // WP-LANDING-EDITOR-ORIENTATION-ENTRY
  // STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_PENDING
  // Query flags only request the coach; they never mutate or publish the site.
  const editorIntroRequested = React.useMemo(() => (
    new URLSearchParams(location.search).get("intro") === "1"
  ), [location.search]);
  const editorIntroSource = React.useMemo(() => (
    new URLSearchParams(location.search).get("source") || "editor"
  ), [location.search]);
  const mobileViewport = useMediaQuery("(max-width:700px)");
  const tabletViewport = useMediaQuery("(max-width:1100px)");
  const {
    projects,
    activeProject,
    setActiveProject,
    loadProjects,
    refreshProjectDeployment,
    retryProjectDeployment,
  } = useProjects();

  const [project, setProject] = React.useState(null);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [gastronomicDialogMode, setGastronomicDialogMode] = React.useState("");
  const [gastronomicActionBusy, setGastronomicActionBusy] = React.useState(false);
  const [gastronomicMenuProductCount, setGastronomicMenuProductCount] = React.useState(null);
  const [gastronomicCountLoading, setGastronomicCountLoading] = React.useState(false);
  const [form, setForm] = React.useState(emptySettings);
  const [device, setDevice] = React.useState(() => (
    mobileViewport ? "mobile" : tabletViewport ? "tablet" : "desktop"
  ));
  const [editorSurface, setEditorSurface] = React.useState("landing");
  const previewDevice = getLandingPreviewDevice(device);
  const adaptiveMobilePreview = mobileViewport && previewDevice.id === "mobile";
  const previewHorizontalGutter = previewDevice.id === "mobile" ? 0 : 32;
  const previewVisualWidth = adaptiveMobilePreview ? "min(390px, 100%)" : `${previewDevice.width}px`;
  const [activeSection, setActiveSection] = React.useState("");
  const [selectedElement, setSelectedElement] = React.useState(null);
  const [elementShellSectionId, setElementShellSectionId] = React.useState("");
  const [elementInspectorOpen, setElementInspectorOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [settingsLoadFailure, setSettingsLoadFailure] = React.useState(null);
  const [settingsRetrying, setSettingsRetrying] = React.useState(false);
  const [savingPath, setSavingPath] = React.useState("");
  const [statusTransition, setStatusTransition] = React.useState("");
  const [previewing, setPreviewing] = React.useState(false);
  const [openingPreview, setOpeningPreview] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [exitDialogOpen, setExitDialogOpen] = React.useState(false);
  const [exitDialogSaving, setExitDialogSaving] = React.useState(false);
  const [exitDialogError, setExitDialogError] = React.useState("");
  const [pendingBlockRemovalId, setPendingBlockRemovalId] = React.useState("");
  const [blockRemovalBusy, setBlockRemovalBusy] = React.useState(false);
  const formRef = React.useRef(emptySettings);
  const savedDraftRef = React.useRef(emptySettings);
  const dirtyRef = React.useRef(false);
  const saveShortcutInFlightRef = React.useRef(false);
  const settingsLoadedProjectRef = React.useRef("");
  const settingsRetryInFlightRef = React.useRef(false);
  const feedbackSequenceRef = React.useRef(0);
  const historyIndexRef = React.useRef(null);
  const restoringBlockedHistoryRef = React.useRef(false);
  const allowConfirmedHistoryExitRef = React.useRef(false);
  const pendingHistoryExitRef = React.useRef(null);
  const pendingAssetDeletesRef = React.useRef(new Set());
  const historySequenceRef = React.useRef(0);
  const editHistoryRef = React.useRef({
    undo: [],
    redo: [],
    undoMeta: [],
    redoMeta: [],
    lastGroupKey: "",
    lastRecordedAt: 0,
  });
  const [historyVersion, setHistoryVersion] = React.useState(0);
  const [revisionsOpen, setRevisionsOpen] = React.useState(false);
  const [revisions, setRevisions] = React.useState([]);
  const [revisionsLoading, setRevisionsLoading] = React.useState(false);
  const [revisionsError, setRevisionsError] = React.useState("");
  const [restoringSiteRevision, setRestoringSiteRevision] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [editorIntroOpen, setEditorIntroOpen] = React.useState(false);
  const editorOpenTrackedRef = React.useRef("");
  const editorSessionStartedAtRef = React.useRef(Date.now());
  const [settingsInitialTab, setSettingsInitialTab] = React.useState(0);
  const [settingsMode, setSettingsMode] = React.useState("global");
  const [sectionId, setSectionId] = React.useState("hero");
  const [segmentInspectorOpen, setSegmentInspectorOpen] = React.useState(false);
  const [segmentInspectorTool, setSegmentInspectorTool] = React.useState("content");
  const [segmentInspectorImagePath, setSegmentInspectorImagePath] = React.useState("");
  const [globalFeedback, setGlobalFeedback] = React.useState(null);
  const [publicationValidation, setPublicationValidation] = React.useState(null);
  const [publicationValidationLoading, setPublicationValidationLoading] = React.useState(false);
  const [publicationValidationError, setPublicationValidationError] = React.useState("");
  const [accessibilityOpen, setAccessibilityOpen] = React.useState(false);
  const [deploymentJob, setDeploymentJob] = React.useState(null);
  const [deploymentChecking, setDeploymentChecking] = React.useState(false);
  const [deploymentAction, setDeploymentAction] = React.useState("");
  const [deploymentMonitorError, setDeploymentMonitorError] = React.useState("");

  const setGlobalFeedbackBySeverity = React.useCallback((severity, nextValue) => {
    const normalizedValue = nextValue == null ? "" : String(nextValue);

    if (!normalizedValue) {
      setGlobalFeedback((currentFeedback) => (
        currentFeedback?.severity === severity ? null : currentFeedback
      ));
      return;
    }

    feedbackSequenceRef.current += 1;
    setGlobalFeedback({
      id: feedbackSequenceRef.current,
      severity,
      message: normalizedValue,
    });
  }, []);

  const setError = React.useCallback(
    (nextValue) => setGlobalFeedbackBySeverity("error", nextValue),
    [setGlobalFeedbackBySeverity]
  );
  const setMessage = React.useCallback(
    (nextValue) => setGlobalFeedbackBySeverity("success", nextValue),
    [setGlobalFeedbackBySeverity]
  );

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [activeProject, projectId, projects]
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const webPushEnabled = isProjectModuleEnabled(effectiveProject, "webpush");
  const gastronomicMenuEnabled = productsEnabled && isProjectCapabilityEnabled(effectiveProject, "gastronomicMenu");
  const canManageProjectSettings = hasProjectPermission(memberAccess, "project.settings.manage");
  const gastronomicPresetInspection = React.useMemo(
    () => inspectGastronomicLandingPreset(form),
    [form]
  );
  const editorSurfaceOptions = React.useMemo(() => ([
    { id: "landing", label: "Página principal" },
    { id: "mega-menu", label: "Megamenú" },
    ...(productsEnabled
      ? [
          { id: "products", label: "Productos" },
          { id: "product", label: "Detalle producto" },
        ]
      : []),
    { id: "contact-page", label: "Página Contacto" },
    ...(webPushEnabled ? [{ id: "notifications", label: "Notificaciones" }] : []),
  ]), [productsEnabled, webPushEnabled]);
  const publicUrl = getProjectPublicUrl(effectiveProject);
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const accessibilityAudit = React.useMemo(
    () => buildLandingAccessibilityAudit(form, productsEnabled),
    [form, productsEnabled]
  );
  const question = React.useMemo(
    () => resolveNextStudioQuestion(progress),
    [progress]
  );
  const saving = Boolean(savingPath);
  const publishing = statusTransition === "published";
  const changingPublicStatus = Boolean(statusTransition);
  const canUndo = editHistoryRef.current.undo.length > 0;
  const canRedo = editHistoryRef.current.redo.length > 0;
  const sessionHistory = React.useMemo(
    () => editHistoryRef.current.undoMeta
      .slice()
      .reverse()
      .map((entry) => ({
        id: entry.id,
        label: entry.label || "Cambio de la página",
        recordedAt: entry.recordedAt,
      })),
    [historyVersion]
  );

  React.useEffect(() => {
    formRef.current = form;
  }, [form]);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const selector = 'link[data-webprofit-landing-fonts="true"]';
    let link = document.head.querySelector(selector);
    const href = String(form?.theme?.googleFontsUrl || "").trim();
    if (!href) {
      link?.remove();
      return undefined;
    }
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.dataset.webprofitLandingFonts = "true";
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
    return undefined;
  }, [form?.theme?.googleFontsUrl]);

  React.useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  React.useEffect(() => {
    const surfaceStillAvailable = editorSurfaceOptions.some((option) => option.id === editorSurface);
    if (surfaceStillAvailable) return;
    setEditorSurface("landing");
  }, [editorSurface, editorSurfaceOptions]);

  React.useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const readHistoryIndex = (state = window.history.state) => (
      Number.isInteger(state?.idx) ? state.idx : null
    );

    historyIndexRef.current = readHistoryIndex();
    restoringBlockedHistoryRef.current = false;
    allowConfirmedHistoryExitRef.current = false;

    const restoredPendingExit = pendingEditorHistoryExit;
    if (
      restoredPendingExit &&
      restoredPendingExit.projectId === String(projectId || "") &&
      restoredPendingExit.originIndex === historyIndexRef.current &&
      isCurrentEditorHash(window.location.hash, projectId)
    ) {
      pendingHistoryExitRef.current = restoredPendingExit;
      setExitDialogError("");
      setExitDialogOpen(true);
    } else {
      pendingHistoryExitRef.current = null;
      if (restoredPendingExit) pendingEditorHistoryExit = null;
    }

    const handleEditorPopState = (event) => {
      const nextIndex = readHistoryIndex(event.state);

      if (allowConfirmedHistoryExitRef.current) {
        if (nextIndex !== null) historyIndexRef.current = nextIndex;
        if (isCurrentEditorHash(window.location.hash, projectId)) {
          allowConfirmedHistoryExitRef.current = false;
        }
        return;
      }

      if (restoringBlockedHistoryRef.current) {
        event.stopImmediatePropagation();
        restoringBlockedHistoryRef.current = false;
        if (nextIndex !== null) historyIndexRef.current = nextIndex;

        const pendingExit = pendingHistoryExitRef.current || pendingEditorHistoryExit;
        if (
          pendingExit &&
          pendingExit.projectId === String(projectId || "") &&
          (nextIndex === null || nextIndex === pendingExit.originIndex) &&
          isCurrentEditorHash(window.location.hash, projectId)
        ) {
          pendingHistoryExitRef.current = pendingExit;
          setExitDialogError("");
          setExitDialogOpen(true);
        }
        return;
      }

      if (!dirtyRef.current || isCurrentEditorHash(window.location.hash, projectId)) {
        if (nextIndex !== null) historyIndexRef.current = nextIndex;
        return;
      }

      const currentIndex = historyIndexRef.current;
      if (currentIndex === null || nextIndex === null || currentIndex === nextIndex) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const pendingExit = {
        projectId: String(projectId || ""),
        originIndex: currentIndex,
        targetIndex: nextIndex,
        delta: nextIndex - currentIndex,
      };
      pendingHistoryExitRef.current = pendingExit;
      pendingEditorHistoryExit = pendingExit;
      restoringBlockedHistoryRef.current = true;
      window.history.go(currentIndex - nextIndex);
    };

    const handleEditorHashChange = (event) => {
      if (allowConfirmedHistoryExitRef.current || !dirtyRef.current) return;
      if (restoringBlockedHistoryRef.current || !isCurrentEditorHash(window.location.hash, projectId)) {
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("popstate", handleEditorPopState, true);
    window.addEventListener("hashchange", handleEditorHashChange, true);
    return () => {
      window.removeEventListener("popstate", handleEditorPopState, true);
      window.removeEventListener("hashchange", handleEditorHashChange, true);
      restoringBlockedHistoryRef.current = false;
      allowConfirmedHistoryExitRef.current = false;
      pendingHistoryExitRef.current = null;
    };
  }, [projectId]);

  const applyDeploymentProject = React.useCallback(
    (nextProject) => {
      if (!nextProject) return null;
      setProject(nextProject);
      setActiveProject(nextProject);
      return nextProject;
    },
    [setActiveProject]
  );

  const refreshDeploymentState = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!projectId || deploymentChecking) return null;
      try {
        if (!silent) setDeploymentChecking(true);
        setDeploymentMonitorError("");
        const nextProject = await refreshProjectDeployment(projectId);
        return applyDeploymentProject(nextProject);
      } catch (err) {
        const nextError = formatApiError(
          err,
          "No se pudo consultar el estado del despliegue"
        );
        setDeploymentMonitorError(nextError);
        if (!silent) setError(nextError);
        return null;
      } finally {
        if (!silent) setDeploymentChecking(false);
      }
    },
    [applyDeploymentProject, deploymentChecking, projectId, refreshProjectDeployment]
  );

  const retryDeployment = React.useCallback(async () => {
    if (!projectId || deploymentAction) return null;
    try {
      setDeploymentAction("retry");
      setDeploymentMonitorError("");
      setError("");

      const failedJobId =
        deploymentJobFailure(deploymentJob) && deploymentJobId(deploymentJob);
      if (failedJobId) {
        const data = await projectsApi.retryDeploymentJob(projectId, failedJobId);
        const retriedJob = data?.job || null;
        if (retriedJob) setDeploymentJob(retriedJob);
        const nextProject = await refreshProjectDeployment(projectId).catch(() => null);
        if (nextProject) applyDeploymentProject(nextProject);
        setMessage(
          "El trabajo de publicación se volvió a poner en cola. Podés seguir su estado desde el editor."
        );
        return retriedJob || data;
      }

      const nextProject = await retryProjectDeployment(projectId);
      setDeploymentJob(null);
      applyDeploymentProject(nextProject);
      setMessage("El despliegue se volvió a iniciar. Podés seguir su estado desde el editor.");
      return nextProject;
    } catch (err) {
      const nextError = formatApiError(err, "No se pudo reintentar el despliegue");
      setDeploymentMonitorError(nextError);
      setError(nextError);
      return null;
    } finally {
      setDeploymentAction("");
    }
  }, [
    applyDeploymentProject,
    deploymentAction,
    deploymentJob,
    projectId,
    refreshProjectDeployment,
    retryProjectDeployment,
  ]);

  const trackedDeploymentJobId = deploymentJobId(deploymentJob);
  const trackedDeploymentJobPending = isPendingDeploymentJob(deploymentJob);

  React.useEffect(() => {
    if (!trackedDeploymentJobId || !trackedDeploymentJobPending) return undefined;

    let active = true;
    const pollJob = async () => {
      try {
        setDeploymentChecking(true);
        const data = await projectsApi.listDeploymentJobs(projectId, {
          type: deploymentJob?.type || "social_preview_refresh",
          limit: 20,
        });
        if (!active) return;
        const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
        const currentJob = jobs.find(
          (job) => deploymentJobId(job) === trackedDeploymentJobId
        );
        if (!currentJob) return;

        setDeploymentJob(currentJob);
        const failure = deploymentJobFailure(currentJob);
        if (failure) {
          setDeploymentMonitorError(failure);
          setError(`La versión quedó publicada, pero el despliegue falló: ${failure}`);
        } else {
          setDeploymentMonitorError("");
        }

        if (["completed", "failed"].includes(String(currentJob.status || "").toLowerCase())) {
          const nextProject = await refreshProjectDeployment(projectId).catch(() => null);
          if (active && nextProject) applyDeploymentProject(nextProject);
        }
      } catch (err) {
        if (active) {
          setDeploymentMonitorError(
            formatApiError(err, "No se pudo seguir el estado del despliegue")
          );
        }
      } finally {
        if (active) setDeploymentChecking(false);
      }
    };

    const initial = window.setTimeout(pollJob, 1800);
    const timer = window.setInterval(pollJob, 4500);
    return () => {
      active = false;
      window.clearTimeout(initial);
      window.clearInterval(timer);
    };
  }, [
    applyDeploymentProject,
    deploymentJob?.type,
    projectId,
    refreshProjectDeployment,
    trackedDeploymentJobId,
    trackedDeploymentJobPending,
  ]);

  React.useEffect(() => {
    const deploymentStatus = String(effectiveProject?.deployment?.status || "").toLowerCase();
    if (deploymentStatus !== "provisioning" || trackedDeploymentJobPending) return undefined;

    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const nextProject = await refreshProjectDeployment(projectId);
        if (active && nextProject) {
          applyDeploymentProject(nextProject);
          setDeploymentMonitorError("");
        }
      } catch {
        // El botón manual de actualizar conserva el error visible si hace falta.
      }
    }, 7000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [
    applyDeploymentProject,
    effectiveProject?.deployment?.status,
    projectId,
    refreshProjectDeployment,
    trackedDeploymentJobPending,
  ]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [loadProjects, projects.length]);

  React.useEffect(() => {
    let active = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!active) return;
        const nextProject = data.project || null;
        setProject(nextProject);
        setMemberAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (nextProject) setActiveProject(nextProject);
      })
      .catch(() => null);

    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  const initializeLoadedSettings = React.useCallback((settings) => {
    const merged = mergeSettings(settings);
    formRef.current = merged;
    savedDraftRef.current = merged;
    settingsLoadedProjectRef.current = String(projectId || "");
    setForm(merged);
    dirtyRef.current = false;
    setDirty(false);
    setSettingsLoadFailure(null);
    editHistoryRef.current = {
      undo: [],
      redo: [],
      undoMeta: [],
      redoMeta: [],
      lastGroupKey: "",
      lastRecordedAt: 0,
    };
    historySequenceRef.current = 0;
    setHistoryVersion((value) => value + 1);

    const initialProgress = getSiteBuilderProgress(merged, productsEnabled);
    const firstIncomplete = initialProgress.steps.find((step) => !step.done);
    const firstSection = firstIncomplete?.actions?.find((action) => action.type === "section")?.section;
    setActiveSection(firstSection || "hero");

    window.dispatchEvent(
      new CustomEvent(TRIAL_LANDING_EVENT, {
        detail: { projectId, settings: merged },
      })
    );

    return merged;
  }, [productsEnabled, projectId]);

  React.useEffect(() => {
    let active = true;
    const requestedProjectId = String(projectId || "");

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await settingsApi.get(projectId);
        if (!active) return;
        initializeLoadedSettings(data.settings);
      } catch (err) {
        if (!active) return;
        const errorMessage = formatApiError(
          err,
          "No se pudo cargar la configuración de la página"
        );
        if (settingsLoadedProjectRef.current === requestedProjectId) {
          setError(errorMessage);
        } else {
          setSettingsLoadFailure({
            projectId: requestedProjectId,
            message: errorMessage,
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [initializeLoadedSettings, projectId, setError]);

  const retrySettingsLoad = React.useCallback(async () => {
    const requestedProjectId = String(projectId || "");
    if (!requestedProjectId || settingsRetryInFlightRef.current) return;

    settingsRetryInFlightRef.current = true;
    setSettingsRetrying(true);
    try {
      const data = await settingsApi.get(projectId);
      initializeLoadedSettings(data.settings);
    } catch (err) {
      const errorMessage = formatApiError(
        err,
        "No se pudo cargar la configuración de la página"
      );
      if (settingsLoadedProjectRef.current === requestedProjectId) {
        setError(errorMessage);
      } else {
        setSettingsLoadFailure({
          projectId: requestedProjectId,
          message: errorMessage,
        });
      }
    } finally {
      settingsRetryInFlightRef.current = false;
      setSettingsRetrying(false);
    }
  }, [initializeLoadedSettings, projectId, setError]);

  const applyDraftState = React.useCallback(
    (nextForm) => {
      const previousAssets = collectManagedAssets(formRef.current);
      const nextAssets = collectManagedAssets(nextForm);
      previousAssets.forEach((path) => {
        if (!nextAssets.has(path)) pendingAssetDeletesRef.current.add(path);
      });
      nextAssets.forEach((path) => pendingAssetDeletesRef.current.delete(path));

      formRef.current = nextForm;
      setForm(nextForm);
      const hasPendingChanges = !sameEditorValue(
        draftSettingsPayload(nextForm, productsEnabled),
        draftSettingsPayload(savedDraftRef.current, productsEnabled)
      );
      dirtyRef.current = hasPendingChanges;
      setDirty(hasPendingChanges);
      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: nextForm },
        })
      );
      return nextForm;
    },
    [productsEnabled, projectId]
  );

  const recordUndoCheckpoint = React.useCallback(
    (snapshot, groupKey = "edit", force = false) => {
      const history = editHistoryRef.current;
      const now = Date.now();
      const coalesced =
        !force &&
        groupKey &&
        history.lastGroupKey === groupKey &&
        now - history.lastRecordedAt < 900;

      if (!coalesced) {
        historySequenceRef.current += 1;
        history.undo.push(snapshot);
        history.undoMeta.push({
          id: `session-${historySequenceRef.current}`,
          label: sessionHistoryLabel(groupKey),
          recordedAt: now,
        });
        if (history.undo.length > 40) {
          const overflow = history.undo.length - 40;
          history.undo.splice(0, overflow);
          history.undoMeta.splice(0, overflow);
        }
      } else if (history.undoMeta.length) {
        history.undoMeta[history.undoMeta.length - 1] = {
          ...history.undoMeta[history.undoMeta.length - 1],
          recordedAt: now,
        };
      }
      history.redo = [];
      history.redoMeta = [];
      history.lastGroupKey = groupKey;
      history.lastRecordedAt = now;
      setHistoryVersion((value) => value + 1);
    },
    []
  );

  const stageDraft = React.useCallback(
    (nextForm, options = {}) => {
      const previous = formRef.current;
      if (
        sameEditorValue(
          draftSettingsPayload(previous, productsEnabled),
          draftSettingsPayload(nextForm, productsEnabled)
        )
      ) {
        return Promise.resolve(previous);
      }

      if (options.recordHistory !== false) {
        recordUndoCheckpoint(
          previous,
          options.historyKey || "edit",
          options.forceHistory === true
        );
      }
      return Promise.resolve(applyDraftState(nextForm));
    },
    [applyDraftState, productsEnabled, recordUndoCheckpoint]
  );

  const loadGastronomicMenuProductCount = React.useCallback(async () => {
    if (!projectId || !productsEnabled) {
      setGastronomicMenuProductCount(0);
      return 0;
    }
    setGastronomicCountLoading(true);
    try {
      const data = await productsApi.list(projectId, { page: 1, pageSize: 500 });
      const products = Array.isArray(data?.products) ? data.products : Array.isArray(data?.items) ? data.items : [];
      const count = products.filter((item) => item?.gastronomicMenu?.enabled === true).length;
      setGastronomicMenuProductCount(count);
      return count;
    } catch (_error) {
      setGastronomicMenuProductCount(null);
      return null;
    } finally {
      setGastronomicCountLoading(false);
    }
  }, [productsEnabled, projectId]);

  const openGastronomicPresetReview = React.useCallback(() => {
    setGastronomicMenuProductCount(null);
    setGastronomicDialogMode("review");
    loadGastronomicMenuProductCount();
  }, [loadGastronomicMenuProductCount]);

  const requestGastronomicLandingAction = React.useCallback(() => {
    if (!productsEnabled) {
      setGastronomicDialogMode("products-required");
      return;
    }
    if (!gastronomicMenuEnabled) {
      setGastronomicDialogMode(canManageProjectSettings ? "activate" : "permission-required");
      return;
    }
    openGastronomicPresetReview();
  }, [canManageProjectSettings, gastronomicMenuEnabled, openGastronomicPresetReview, productsEnabled]);

  const activateGastronomicMenu = React.useCallback(async () => {
    if (!productsEnabled || !canManageProjectSettings || gastronomicActionBusy) return;
    setGastronomicActionBusy(true);
    try {
      const capabilities = {
        ...effectiveProjectCapabilities(effectiveProject || {}),
        gastronomicMenu: true,
      };
      const data = await projectsApi.updateBusinessConfiguration(projectId, { capabilities });
      const nextProject = data?.project || null;
      if (!nextProject || !isProjectCapabilityEnabled(nextProject, "gastronomicMenu")) {
        throw new Error("El servidor no confirmó la activación de Carta.");
      }
      setProject(nextProject);
      setActiveProject(nextProject);
      await loadProjects().catch(() => null);
      setMessage("Carta gastronómica activada. La página web todavía no fue modificada.");
      openGastronomicPresetReview();
    } catch (err) {
      setError(formatApiError(err, "No se pudo activar Carta gastronómica"));
    } finally {
      setGastronomicActionBusy(false);
    }
  }, [
    canManageProjectSettings,
    effectiveProject,
    gastronomicActionBusy,
    loadProjects,
    openGastronomicPresetReview,
    productsEnabled,
    projectId,
    setActiveProject,
    setError,
    setMessage,
  ]);

  const applyGastronomicLandingPreset = React.useCallback(async () => {
    if (gastronomicActionBusy) return;
    const inspection = inspectGastronomicLandingPreset(formRef.current);
    if (!inspection.changed) {
      setGastronomicDialogMode("");
      setMessage("Tu web ya está adaptada o no encontramos defaults seguros para modificar.");
      return;
    }
    setGastronomicActionBusy(true);
    try {
      await stageDraft(inspection.nextForm, {
        historyKey: "gastronomic-preset",
        forceHistory: true,
      });
      setGastronomicDialogMode("");
      setMessage("Adaptación para Carta aplicada al borrador. Guardá cuando quieras conservarla.");
    } finally {
      setGastronomicActionBusy(false);
    }
  }, [gastronomicActionBusy, setMessage, stageDraft]);

  const undoLastEdit = React.useCallback(() => {
    const history = editHistoryRef.current;
    if (!history.undo.length) return;

    const current = formRef.current;
    const previous = history.undo.pop();
    const checkpointMeta = history.undoMeta.pop() || {
      id: `session-fallback-${Date.now()}`,
      label: "Cambio de la página",
      recordedAt: Date.now(),
    };
    history.redo.push(current);
    history.redoMeta.push(checkpointMeta);
    if (history.redo.length > 40) {
      history.redo.shift();
      history.redoMeta.shift();
    }
    history.lastGroupKey = "";
    history.lastRecordedAt = 0;

    applyDraftState(preserveVersionMetadata(previous, current));
    setHistoryVersion((value) => value + 1);
    setMessage("Se deshizo el último cambio. Guardá cuando quieras conservar esta recuperación.");
  }, [applyDraftState]);

  const redoLastEdit = React.useCallback(() => {
    const history = editHistoryRef.current;
    if (!history.redo.length) return;

    const current = formRef.current;
    const next = history.redo.pop();
    const checkpointMeta = history.redoMeta.pop() || {
      id: `session-fallback-${Date.now()}`,
      label: "Cambio de la página",
      recordedAt: Date.now(),
    };
    history.undo.push(current);
    history.undoMeta.push(checkpointMeta);
    if (history.undo.length > 40) {
      history.undo.shift();
      history.undoMeta.shift();
    }
    history.lastGroupKey = "";
    history.lastRecordedAt = 0;

    applyDraftState(preserveVersionMetadata(next, current));
    setHistoryVersion((value) => value + 1);
    setMessage("Se rehizo el cambio.");
  }, [applyDraftState]);

  const restoreSessionCheckpoint = React.useCallback((checkpointId) => {
    const history = editHistoryRef.current;
    const targetIndex = history.undoMeta.findIndex((entry) => entry.id === checkpointId);
    if (targetIndex < 0) return;

    const steps = history.undoMeta.length - targetIndex;
    for (let step = 0; step < steps; step += 1) {
      undoLastEdit();
    }
    setMessage(
      steps === 1
        ? "Volviste al estado anterior de ese cambio."
        : `Volviste ${steps} cambios atrás. Podés rehacerlos si lo necesitás.`
    );
  }, [undoLastEdit]);

  React.useEffect(() => {
    if (loading || saving || changingPublicStatus || previewing || openingPreview || restoringSiteRevision) {
      return undefined;
    }

    const handleEditorHistoryShortcut = (event) => {
      if (event.defaultPrevented || event.isComposing || isEditableKeyboardTarget(event.target)) return;
      if (event.altKey || (!event.ctrlKey && !event.metaKey)) return;

      const key = String(event.key || "").toLowerCase();
      const redoShortcut =
        (key === "z" && event.shiftKey) ||
        (key === "y" && event.ctrlKey && !event.metaKey && !event.shiftKey);
      const undoShortcut = key === "z" && !event.shiftKey;

      if (redoShortcut) {
        if (!canRedo) return;
        event.preventDefault();
        redoLastEdit();
        return;
      }

      if (undoShortcut) {
        if (!canUndo) return;
        event.preventDefault();
        undoLastEdit();
      }
    };

    window.addEventListener("keydown", handleEditorHistoryShortcut);
    return () => window.removeEventListener("keydown", handleEditorHistoryShortcut);
  }, [
    canRedo,
    canUndo,
    changingPublicStatus,
    loading,
    openingPreview,
    previewing,
    redoLastEdit,
    restoringSiteRevision,
    saving,
    undoLastEdit,
  ]);

  const focusLandingCopyError = React.useCallback((snapshot, fieldPath) => {
    let nextSectionId = copySectionIdForPath(fieldPath);
    if (nextSectionId === "custom") {
      const match = String(fieldPath).match(/^layout\.segments\.(\d+)\./);
      const index = match ? Number(match[1]) : -1;
      nextSectionId = snapshot?.layout?.segments?.[index]?.id || "";
    }
    if (!nextSectionId) return;

    setPreviewing(false);
    setEditorSurface("landing");
    setSettingsOpen(false);
    setAccessibilityOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setActiveSection(nextSectionId);

    const context = getElementSectionContext(snapshot, nextSectionId);
    if (!context.definition) return;

    const exactSelection = resolveEditableElementSelection(snapshot, {
      sectionId: nextSectionId,
      sectionType: context.sectionType,
      fieldPath: String(fieldPath || "").trim(),
      logicalType: "field",
      previewDevice: previewDevice.id,
    });

    if (exactSelection) {
      setSelectedElement(exactSelection);
      setElementInspectorOpen(true);
      return;
    }

    if (context.definition.elementEditingReady === true) {
      if (context.definition.defaultElementId) {
        setSelectedElement({
          sectionId: nextSectionId,
          sectionType: context.sectionType,
          fieldPath: "",
          elementId: context.definition.defaultElementId,
          logicalType: "complex",
          previewDevice: previewDevice.id,
        });
      } else {
        setElementShellSectionId(nextSectionId);
      }
      setElementInspectorOpen(true);
      return;
    }

    setSectionId(nextSectionId);
    setSegmentInspectorTool("content");
    setSegmentInspectorImagePath("");
    setSegmentInspectorOpen(true);
  }, [previewDevice.id]);

  const validateCopyDraft = React.useCallback((snapshot = formRef.current, focusFirst = true) => {
    const validation = validateLandingCopyDraft(snapshot, savedDraftRef.current);
    if (validation.valid) return validation;

    const first = validation.errors[0];
    const count = validation.errors.length;
    setError(
      `Hay ${count} ${count === 1 ? "texto que supera" : "textos que superan"} el límite. ` +
      `${first?.label || first?.path || "Revisá el copy"}: ${first?.length || 0}/${first?.max || 0}. Corregilo antes de guardar.`
    );

    if (focusFirst && first?.path) {
      focusLandingCopyError(snapshot, first.path);
    }

    return validation;
  }, [focusLandingCopyError, setError]);

  const saveDraft = React.useCallback(async () => {
    const snapshot = formRef.current;
    const copyValidation = validateCopyDraft(snapshot);
    if (!copyValidation.valid) return null;
    const pendingDeletesAtStart = new Set(pendingAssetDeletesRef.current);
    try {
      setSavingPath("draft");
      setError("");
      const data = await settingsApi.update(
        projectId,
        draftSettingsPayload(snapshot, productsEnabled)
      );
      const merged = mergeSettings(data.settings);
      const changedDuringSave = formRef.current !== snapshot;
      const nextForm = changedDuringSave
        ? preserveVersionMetadata(formRef.current, merged)
        : merged;
      savedDraftRef.current = merged;
      formRef.current = nextForm;
      setForm(nextForm);
      const hasPendingChanges = !sameEditorValue(
        draftSettingsPayload(nextForm, productsEnabled),
        draftSettingsPayload(merged, productsEnabled)
      );
      dirtyRef.current = hasPendingChanges;
      setDirty(hasPendingChanges);

      const currentAssets = collectManagedAssets(formRef.current);
      const pendingDeletes = Array.from(pendingDeletesAtStart).filter(
        (path) => isManagedLandingAsset(path) && !currentAssets.has(path)
      );
      pendingDeletesAtStart.forEach((path) => {
        if (!currentAssets.has(path)) pendingAssetDeletesRef.current.delete(path);
      });
      await Promise.allSettled(
        pendingDeletes.map((path) => landingAssetsApi.remove(projectId, path))
      );

      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: nextForm },
        })
      );
      setMessage(
        hasPendingChanges
          ? "Borrador guardado. Hay cambios nuevos todavía pendientes."
          : "Borrador guardado. La versión publicada no cambió."
      );
      return nextForm;
    } catch (err) {
      dirtyRef.current = true;
      setDirty(true);
      setError(formatApiError(err, "No se pudo guardar el borrador"));
      return null;
    } finally {
      setSavingPath("");
    }
  }, [productsEnabled, projectId, validateCopyDraft]);

  React.useEffect(() => {
    const handleEditorSaveShortcut = (event) => {
      if (event.isComposing || event.altKey || event.shiftKey) return;
      if (!event.ctrlKey && !event.metaKey) return;
      if (String(event.key || "").toLowerCase() !== "s") return;

      event.preventDefault();

      if (
        loading ||
        saving ||
        changingPublicStatus ||
        openingPreview ||
        restoringSiteRevision ||
        saveShortcutInFlightRef.current ||
        !dirtyRef.current
      ) {
        return;
      }

      saveShortcutInFlightRef.current = true;
      Promise.resolve(saveDraft()).finally(() => {
        saveShortcutInFlightRef.current = false;
      });
    };

    window.addEventListener("keydown", handleEditorSaveShortcut);
    return () => window.removeEventListener("keydown", handleEditorSaveShortcut);
  }, [
    changingPublicStatus,
    loading,
    openingPreview,
    restoringSiteRevision,
    saveDraft,
    saving,
  ]);

  const saveSettingsDialog = React.useCallback(
    async (nextForm) => {
      await stageDraft(nextForm, {
        historyKey: "settings-dialog",
        forceHistory: true,
      });
      const saved = await saveDraft();
      if (!saved) {
        throw new Error("No se pudieron guardar los ajustes del borrador.");
      }
      return saved;
    },
    [saveDraft, stageDraft]
  );

  const commitPath = React.useCallback(
    (path, value) => {
      const next = withExperienceDefaultOverrides(
        setValueAtPath(formRef.current, path, value),
        { [path]: value }
      );
      return stageDraft(next, { historyKey: `path:${path}` });
    },
    [stageDraft]
  );

  const commitMany = React.useCallback(
    (changes) => {
      const next = withExperienceDefaultOverrides(
        setManyValuesAtPaths(formRef.current, changes),
        changes
      );
      const keys = Object.keys(changes || {}).sort().join("|");
      return stageDraft(next, { historyKey: `group:${keys || "changes"}` });
    },
    [stageDraft]
  );

  const uploadAssetFile = React.useCallback(
    async (file, metadata = {}) => {
      const response = await landingAssetsApi.upload(projectId, file, {
        pageKey: "home",
        ...metadata,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El servidor no devolvió la ruta del archivo");
      return { ...response.asset, value };
    },
    [projectId]
  );

  const deferAssetDeletion = React.useCallback(
    async () => ({ ok: true, removed: false, pending: true }),
    []
  );

  const uploadInlineImage = React.useCallback(
    async (path, file, metadata = {}) => {
      try {
        setSavingPath(`asset:${path}`);
        const brandingAssetKey = {
          "theme.logoUrl": "logo",
          "theme.compactLogoUrl": "compact-logo",
          "theme.faviconUrl": "favicon",
        }[String(path || "")];
        const asset = await uploadAssetFile(
          file,
          brandingAssetKey
            ? { ...metadata, sectionKey: "branding", assetKey: brandingAssetKey }
            : metadata
        );
        const next = setValueAtPath(formRef.current, path, asset.value);
        return await stageDraft(next, {
          historyKey: `asset:${path}`,
          forceHistory: true,
        });
      } catch (err) {
        setError(err.message || "No se pudo subir la imagen");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [stageDraft, uploadAssetFile]
  );

  const removeInlineImage = React.useCallback(
    async (path) => {
      const current = formRef.current;

      try {
        const next = setValueAtPath(current, path, "");
        return await stageDraft(next, {
          historyKey: `asset:${path}`,
          forceHistory: true,
        });
      } catch (err) {
        setError(err.message || "No se pudo preparar la eliminación de la imagen");
        throw err;
      }
    },
    [stageDraft]
  );

  const openSettingsAt = React.useCallback((tab = 0, mode = "global") => {
    setAccessibilityOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setSettingsMode(mode);
    setSettingsInitialTab(tab);
    setSettingsOpen(true);
  }, []);

  const openSection = React.useCallback((nextSection, tool = "content") => {
    setAccessibilityOpen(false);
    setSettingsOpen(false);
    setActiveSection(nextSection);
    const layoutSegment = getLandingSegments(formRef.current).find((segment) => segment.id === nextSection);
    const definition = getElementSectionContext(formRef.current, nextSection)?.definition || getSegmentDefinition(layoutSegment?.type || nextSection);
    if (definition?.elementEditingReady === true) {
      setSegmentInspectorOpen(false);
      if (definition.defaultElementId) {
        setElementShellSectionId("");
        setSelectedElement({
          sectionId: nextSection,
          sectionType: layoutSegment?.type || nextSection,
          fieldPath: "",
          elementId: definition.defaultElementId,
          logicalType: "complex",
          previewDevice: previewDevice.id,
        });
      } else {
        setSelectedElement(null);
        setElementShellSectionId(nextSection);
      }
      setElementInspectorOpen(true);
      return;
    }
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setSectionId(nextSection);
    setSegmentInspectorTool(tool);
    setSegmentInspectorImagePath("");
    setSegmentInspectorOpen(true);
  }, [previewDevice.id]);

  const runAction = React.useCallback(
    (action) => {
      if (!action) return;
      if (action.type === "section" && action.section) {
        if (action.section === "identity") {
          openSettingsAt(3, "identity");
          return;
        }
        if (getSegmentDefinition(action.section)) {
          openSection(action.section, action.tool || "content");
          return;
        }
      }
      const requestedTab = Number(action.tab || 0);
      if (requestedTab === 2) openSettingsAt(2, "sections");
      else if (requestedTab === 3) openSettingsAt(3, "identity");
      else openSettingsAt(requestedTab, "global");
    },
    [openSection, openSettingsAt]
  );

  const selectProgressStep = React.useCallback(
    (step) => {
      const firstSection = step?.actions?.find((action) => action.type === "section");
      runAction(firstSection || step?.actions?.[0]);
    },
    [runAction]
  );

  const selectCanvasSection = React.useCallback(
    (nextSection) => {
      const layoutSegment = getLandingSegments(formRef.current).find((segment) => segment.id === nextSection);
      const sectionType = layoutSegment?.type || nextSection;
      const relatedStep = findStepForSection(sectionType, productsEnabled);
      if (relatedStep || layoutSegment || nextSection) setActiveSection(nextSection);
      const definition = getElementSectionContext(formRef.current, nextSection)?.definition || getSegmentDefinition(sectionType);
      if (definition?.elementEditingReady === true) {
        setSettingsOpen(false);
        setAccessibilityOpen(false);
        setSegmentInspectorOpen(false);
        if (definition.defaultElementId) {
          setElementShellSectionId("");
          setSelectedElement({
            sectionId: nextSection,
            sectionType,
            fieldPath: "",
            elementId: definition.defaultElementId,
            logicalType: "complex",
            previewDevice: previewDevice.id,
          });
        } else {
          setSelectedElement(null);
          setElementShellSectionId(nextSection);
        }
        setElementInspectorOpen(true);
      }
    },
    [productsEnabled, previewDevice.id]
  );

  const selectCanvasElement = React.useCallback((nextElement) => {
    if (!nextElement?.sectionId) return;
    setSettingsOpen(false);
    setAccessibilityOpen(false);
    setSegmentInspectorOpen(false);
    setActiveSection(nextElement.sectionId);
    setElementShellSectionId("");
    setSelectedElement(nextElement);
    setElementInspectorOpen(true);
  }, []);

  const openSegmentTool = React.useCallback((nextSection, tool = "content", imagePath = "") => {
    setAccessibilityOpen(false);
    setSettingsOpen(false);
    setActiveSection(nextSection);
    const layoutSegment = getLandingSegments(formRef.current).find((segment) => segment.id === nextSection);
    const definition = getElementSectionContext(formRef.current, nextSection)?.definition || getSegmentDefinition(layoutSegment?.type || nextSection);
    if (definition?.elementEditingReady === true) {
      setSegmentInspectorOpen(false);
      if (definition.defaultElementId) {
        setElementShellSectionId("");
        setSelectedElement({
          sectionId: nextSection,
          sectionType: layoutSegment?.type || nextSection,
          fieldPath: "",
          elementId: definition.defaultElementId,
          logicalType: "complex",
          previewDevice: previewDevice.id,
        });
      } else {
        setSelectedElement(null);
        setElementShellSectionId(nextSection);
      }
      setElementInspectorOpen(true);
      return;
    }
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setSectionId(nextSection);
    setSegmentInspectorTool(tool);
    setSegmentInspectorImagePath(tool === "image" ? String(imagePath || "") : "");
    setSegmentInspectorOpen(true);
  }, [previewDevice.id]);

  const openAccessibilityReview = React.useCallback(() => {
    setPreviewing(false);
    setSettingsOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setActiveSection("");
    setAccessibilityOpen(true);
  }, []);

  const reviewAccessibilityIssue = React.useCallback((issue) => {
    setAccessibilityOpen(false);
    setPreviewing(false);
    setEditorSurface("landing");

    const nextSection = String(issue?.section || "").trim();
    if (!nextSection) return;

    const context = getElementSectionContext(formRef.current, nextSection);
    if (!context.definition) return;

    const exactSelection = resolveEditableElementSelection(formRef.current, {
      sectionId: nextSection,
      sectionType: context.sectionType,
      fieldPath: String(issue?.path || "").trim(),
      logicalType: "field",
      previewDevice: previewDevice.id,
    });

    if (exactSelection) {
      selectCanvasElement(exactSelection);
      return;
    }

    openSegmentTool(nextSection, issue.tool || "content");
  }, [openSegmentTool, previewDevice.id, selectCanvasElement]);

  const toggleCanvasSection = React.useCallback(
    (nextSection, nextVisible) => {
      const currentForm = formRef.current;
      const rawSegments = Array.isArray(currentForm?.layout?.segments) ? currentForm.layout.segments : [];
      const editableInstanceIndex = rawSegments.findIndex(
        (segment) => segment?.id === nextSection &&
          !isStructuralLandingType(segment?.type) &&
          !isCanonicalLandingSegment(segment)
      );

      if (editableInstanceIndex >= 0) {
        commitPath(`layout.segments.${editableInstanceIndex}.visible`, nextVisible === true);
        if (!nextVisible) {
          setActiveSection("");
          if (sectionId === nextSection) setSegmentInspectorOpen(false);
          if (selectedElement?.sectionId === nextSection || elementShellSectionId === nextSection) {
            setElementInspectorOpen(false);
            setSelectedElement(null);
            setElementShellSectionId("");
          }
        }
        return;
      }

      const definition = getSegmentDefinition(nextSection);
      if (!definition?.visibilityPath) return;

      const changes = buildSegmentVisibilityChanges(nextSection, nextVisible);
      if (!Object.keys(changes).length) return;

      commitMany(changes);
      if (!nextVisible) {
        setActiveSection("");
        if (sectionId === nextSection) setSegmentInspectorOpen(false);
        if (selectedElement?.sectionId === nextSection || elementShellSectionId === nextSection) {
          setElementInspectorOpen(false);
          setSelectedElement(null);
          setElementShellSectionId("");
        }
      }
    },
    [commitMany, commitPath, elementShellSectionId, sectionId, selectedElement?.sectionId]
  );

  const requestRemoveCanvasBlock = React.useCallback((nextSection) => {
    const segment = getLandingSegments(formRef.current).find((candidate) => candidate.id === nextSection);
    if (!segment || isStructuralLandingType(segment.type)) return;
    setPendingBlockRemovalId(segment.id);
  }, []);

  const pendingBlockRemoval = React.useMemo(() => (
    getLandingSegments(form).find((segment) => segment.id === pendingBlockRemovalId) || null
  ), [form, pendingBlockRemovalId]);

  const pendingBlockRemovalLabel = React.useMemo(() => {
    if (!pendingBlockRemoval) return "este bloque";
    const customTitle = String(pendingBlockRemoval?.data?.custom?.title || "").trim();
    if (customTitle) return customTitle;
    const definition = getSegmentDefinition(pendingBlockRemoval.type);
    return definition?.label || "este bloque";
  }, [pendingBlockRemoval]);

  const confirmRemoveCanvasBlock = React.useCallback(async () => {
    const current = formRef.current;
    const segment = getLandingSegments(current).find((candidate) => candidate.id === pendingBlockRemovalId);
    if (!segment || isStructuralLandingType(segment.type)) {
      setPendingBlockRemovalId("");
      return;
    }

    try {
      setBlockRemovalBusy(true);
      let nextForm = {
        ...current,
        layout: removeLandingSegment(current.layout, segment.id),
      };
      if (isCanonicalLandingSegment(segment)) {
        nextForm = setManyValuesAtPaths(
          nextForm,
          buildSegmentVisibilityChanges(segment.type, false)
        );
      }
      await stageDraft(nextForm, {
        historyKey: `layout:remove:${segment.id}`,
        forceHistory: true,
      });

      if (activeSection === segment.id) setActiveSection("");
      if (sectionId === segment.id) setSegmentInspectorOpen(false);
      if (selectedElement?.sectionId === segment.id || elementShellSectionId === segment.id) {
        setElementInspectorOpen(false);
        setSelectedElement(null);
        setElementShellSectionId("");
      }
      setPendingBlockRemovalId("");
      setMessage(`Bloque “${pendingBlockRemovalLabel}” eliminado del borrador. Guardá cuando quieras conservar el cambio.`);
    } catch (err) {
      setError(err?.message || "No se pudo eliminar el bloque");
    } finally {
      setBlockRemovalBusy(false);
    }
  }, [
    activeSection,
    elementShellSectionId,
    pendingBlockRemovalId,
    pendingBlockRemovalLabel,
    sectionId,
    selectedElement?.sectionId,
    setError,
    setMessage,
    stageDraft,
  ]);

  const changeEditorSurface = React.useCallback((nextSurface) => {
    const allowed = editorSurfaceOptions.some((option) => option.id === nextSurface);
    if (!allowed) return;
    setPreviewing(false);
    setSettingsOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setAccessibilityOpen(false);
    setActiveSection("");
    setEditorSurface(nextSurface);
  }, [editorSurfaceOptions]);

  const togglePreview = React.useCallback(() => {
    setSettingsOpen(false);
    setSegmentInspectorOpen(false);
    setElementInspectorOpen(false);
    setSelectedElement(null);
    setElementShellSectionId("");
    setActiveSection("");
    setPreviewing((current) => !current);
  }, []);

  const openSecurePreview = React.useCallback(async () => {
    if (!publicUrl) {
      setError("La página pública todavía no tiene una URL disponible para la vista previa.");
      return null;
    }

    const previewWindow = openPreviewPlaceholder();
    if (!previewWindow) {
      setError("El navegador bloqueó la nueva pestaña. Habilitá las ventanas emergentes e intentá nuevamente.");
      return null;
    }

    try {
      setOpeningPreview(true);
      setError("");

      if (dirty) {
        const savedDraft = await saveDraft();
        if (!savedDraft) {
          previewWindow.close();
          return null;
        }
      }

      const data = await settingsApi.createPreviewToken(projectId);
      const preview = data?.preview || null;
      const previewUrl = buildProjectSecurePreviewUrl(
        effectiveProject,
        preview,
        getApiBaseUrl()
      );

      if (!previewUrl) {
        throw new Error("El servidor no devolvió una vista previa válida para esta página.");
      }
      if (previewWindow.closed) {
        throw new Error("La pestaña de vista previa se cerró antes de completar la apertura.");
      }

      previewWindow.location.replace(previewUrl);
      setMessage(
        `Vista previa segura abierta para el borrador v${preview?.draftRevision || formRef.current?.draftRevision || 1}.`
      );
      return preview;
    } catch (err) {
      if (!previewWindow.closed) previewWindow.close();
      setError(err.message || "No se pudo abrir la vista previa segura");
      return null;
    } finally {
      setOpeningPreview(false);
    }
  }, [dirty, effectiveProject, projectId, publicUrl, saveDraft]);

  const loadPublishedRevisions = React.useCallback(async () => {
    try {
      setRevisionsLoading(true);
      setRevisionsError("");
      const data = await settingsApi.listRevisions(projectId);
      setRevisions(Array.isArray(data?.revisions) ? data.revisions : []);
      return data;
    } catch (err) {
      setRevisionsError(
        formatApiError(err, "No se pudo cargar el historial de publicaciones")
      );
      return null;
    } finally {
      setRevisionsLoading(false);
    }
  }, [projectId]);

  const openPublishedRevisions = React.useCallback(() => {
    setRevisionsOpen(true);
    loadPublishedRevisions();
  }, [loadPublishedRevisions]);

  const restorePublishedRevision = React.useCallback(
    async (revision) => {
      const siteRevision = Number(revision?.siteRevision || 0);
      if (!Number.isInteger(siteRevision) || siteRevision < 1) {
        setError("La publicación seleccionada ya no tiene una revisión válida.");
        return null;
      }


      const beforeRestore = formRef.current;
      try {
        setRestoringSiteRevision(siteRevision);
        setRevisionsError("");
        setError("");
        const data = await settingsApi.restoreRevision(projectId, siteRevision);
        const merged = mergeSettings(data?.settings);
        const contentChanged = !sameEditorValue(
          draftSettingsPayload(beforeRestore, productsEnabled),
          draftSettingsPayload(merged, productsEnabled)
        );

        if (contentChanged) {
          recordUndoCheckpoint(
            beforeRestore,
            `restore:${siteRevision}`,
            true
          );
        }

        pendingAssetDeletesRef.current.clear();
        savedDraftRef.current = merged;
        formRef.current = merged;
        setForm(merged);
        dirtyRef.current = false;
        setDirty(false);
        setRevisionsOpen(false);

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );

        setMessage(
          contentChanged
            ? "La versión elegida se recuperó como borrador. La web publicada no cambió."
            : "El borrador ya coincidía con esa publicación. No se creó una revisión duplicada."
        );
        return data;
      } catch (err) {
        const message = formatApiError(
          err,
          "No se pudo recuperar la publicación seleccionada"
        );
        setRevisionsError(message);
        setError(message);
        return null;
      } finally {
        setRestoringSiteRevision(null);
      }
    },
    [productsEnabled, projectId, recordUndoCheckpoint]
  );

  const applyPublicStatus = React.useCallback(
    async (targetStatus) => {
      const publicStatus = String(targetStatus || "").trim().toLowerCase();
      if (!["draft", "published", "maintenance"].includes(publicStatus)) {
        setError("El estado público seleccionado no es válido.");
        return null;
      }
      if (statusTransition) return null;

      if (dirtyRef.current) {
        const savedDraft = await saveDraft();
        if (!savedDraft) return null;
      }

      try {
        setStatusTransition(publicStatus);
        setError("");
        setDeploymentMonitorError("");
        const data =
          publicStatus === "published"
            ? await settingsApi.publish(projectId)
            : await settingsApi.setPublicStatus(projectId, publicStatus);
        const publication = data.publication || {};
        const returnedSettings = data.settings || data.publishedSettings || null;
        const base = returnedSettings
          ? mergeSettings(returnedSettings)
          : formRef.current;
        const revision = Number(
          data.revision ?? publication.revision ?? returnedSettings?.revision ?? base.revision ?? 1
        );
        const draftRevision = Number(
          data.draftRevision ??
            publication.draftRevision ??
            returnedSettings?.draftRevision ??
            base.draftRevision ??
            revision
        );
        const publishedRevision = Number(
          data.publishedRevision ??
            publication.publishedRevision ??
            returnedSettings?.publishedRevision ??
            base.publishedRevision ??
            0
        );
        const nextForm = {
          ...base,
          revision,
          draftRevision,
          publicStatus:
            data.publicStatus ||
            publication.publicStatus ||
            returnedSettings?.publicStatus ||
            publicStatus,
          publishedRevision,
        };
        formRef.current = nextForm;
        setForm(nextForm);
        dirtyRef.current = false;
        setDirty(false);

        const deployment = deploymentFromPublishResponse(data);
        let nextDeploymentJob = deploymentJobFromPublishResponse(data);
        if (!nextDeploymentJob && deployment?.refreshQueued === true) {
          try {
            const jobsData = await projectsApi.listDeploymentJobs(projectId, {
              type: "social_preview_refresh",
              limit: 5,
            });
            const jobs = Array.isArray(jobsData?.jobs) ? jobsData.jobs : [];
            nextDeploymentJob = jobs[0] || null;
          } catch (jobError) {
            setDeploymentMonitorError(
              formatApiError(
                jobError,
                "La publicación se completó, pero no se pudo consultar su despliegue"
              )
            );
          }
        }
        setDeploymentJob(nextDeploymentJob);
        const returnedProject = data.project || null;
        if (returnedProject || deployment) {
          const nextProject = {
            ...(effectiveProject || {}),
            ...(returnedProject || {}),
            deployment: {
              ...(effectiveProject?.deployment || {}),
              ...(returnedProject?.deployment || {}),
              ...(deployment || {}),
            },
          };
          setProject(nextProject);
          setActiveProject(nextProject);
        }

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: nextForm },
          })
        );

        if (nextForm.publicStatus === "published") {
          const deploymentStatus = String(deployment?.status || "").trim();
          const deploymentQueued = deployment?.refreshQueued === true;
          const publicationChanged = publication.changed !== false;
          setMessage(
            !publicationChanged
              ? `El borrador v${draftRevision} ya era la versión publicada. No se creó otra revisión.`
              : deploymentQueued
                ? `Revisión ${publishedRevision} publicada. El despliegue quedó en cola y se seguirá desde el editor.`
                : deploymentStatus === "live"
                  ? `Sitio publicado en la revisión ${publishedRevision}.`
                  : `Revisión ${publishedRevision} publicada. Estado del despliegue: ${deploymentStatus || "sin despliegue automático"}.`
          );
        } else if (nextForm.publicStatus === "maintenance") {
          setMessage(
            `Modo mantenimiento activado. La publicación ${publishedRevision || "anterior"} se conservó.`
          );
        } else {
          setMessage(
            "Sitio oculto como borrador. La última publicación y el historial se conservaron."
          );
        }
        return data;
      } catch (err) {
        setError(formatApiError(err, "No se pudo cambiar el estado público del sitio"));
        return null;
      } finally {
        setStatusTransition("");
      }
    },
    [effectiveProject, projectId, saveDraft, setActiveProject, statusTransition]
  );

  const validatePublication = React.useCallback(async () => {
    if (dirtyRef.current) {
      setPublicationValidation(null);
      setPublicationValidationError("Guardá el borrador antes de validarlo para publicación.");
      return null;
    }
    try {
      setPublicationValidationLoading(true);
      setPublicationValidationError("");
      const data = await settingsApi.validatePublication(projectId);
      const validation = data?.validation || null;
      setPublicationValidation(validation);
      return validation;
    } catch (err) {
      const message = formatApiError(err, "No se pudo validar el borrador antes de publicar");
      setPublicationValidation(null);
      setPublicationValidationError(message);
      return null;
    } finally {
      setPublicationValidationLoading(false);
    }
  }, [projectId]);

  const publishDraft = React.useCallback(async () => {
    if (dirtyRef.current) {
      const copyValidation = validateCopyDraft(formRef.current);
      if (!copyValidation.valid) return null;
      setError("Guardá el borrador antes de publicarlo.");
      return null;
    }
    const validation = await validatePublication();
    if (!validation) {
      setError("No se pudo completar la validación previa a la publicación.");
      return null;
    }
    if (validation.canPublish !== true) {
      setError("Corregí los problemas bloqueantes indicados antes de publicar.");
      return null;
    }
    return applyPublicStatus("published");
  }, [applyPublicStatus, validateCopyDraft, validatePublication]);

  const navigateBackToProjectPanel = React.useCallback(() => {
    allowConfirmedHistoryExitRef.current = true;
    try {
      navigate(`/dashboard/proyectos/${projectId}/pagina`);
    } catch (navigationError) {
      allowConfirmedHistoryExitRef.current = false;
      throw navigationError;
    }
  }, [navigate, projectId]);

  const completeEditorExit = React.useCallback(() => {
    const pendingHistoryExit = pendingHistoryExitRef.current || pendingEditorHistoryExit;
    pendingHistoryExitRef.current = null;
    pendingEditorHistoryExit = null;

    if (pendingHistoryExit?.delta) {
      allowConfirmedHistoryExitRef.current = true;
      window.history.go(pendingHistoryExit.delta);
      return;
    }

    navigateBackToProjectPanel();
  }, [navigateBackToProjectPanel]);

  const cancelEditorExit = React.useCallback(() => {
    if (exitDialogSaving || saving) return;
    pendingHistoryExitRef.current = null;
    pendingEditorHistoryExit = null;
    setExitDialogError("");
    setExitDialogOpen(false);
  }, [exitDialogSaving, saving]);

  const discardChangesAndLeave = React.useCallback(() => {
    if (exitDialogSaving || saving) return;
    setExitDialogError("");
    setExitDialogOpen(false);
    completeEditorExit();
  }, [completeEditorExit, exitDialogSaving, saving]);

  const saveAndLeaveEditor = React.useCallback(async () => {
    if (exitDialogSaving || saving) return;

    if (!dirtyRef.current) {
      setExitDialogError("");
      setExitDialogOpen(false);
      completeEditorExit();
      return;
    }

    try {
      setExitDialogSaving(true);
      setExitDialogError("");
      const savedDraft = await saveDraft();

      if (!savedDraft) {
        setExitDialogError(
          "No pudimos guardar el borrador. Tus cambios siguen pendientes. Podés reintentar, salir sin guardar o cancelar."
        );
        return;
      }

      if (dirtyRef.current) {
        setExitDialogError(
          "El borrador se guardó, pero quedaron cambios nuevos sin guardar. Revisalos o volvé a intentar Guardar y salir."
        );
        return;
      }

      setExitDialogOpen(false);
      completeEditorExit();
    } finally {
      setExitDialogSaving(false);
    }
  }, [completeEditorExit, exitDialogSaving, saveDraft, saving]);

  const leaveEditor = React.useCallback(() => {
    pendingHistoryExitRef.current = null;
    pendingEditorHistoryExit = null;
    if (!dirtyRef.current) {
      navigateBackToProjectPanel();
      return;
    }
    setExitDialogError("");
    setExitDialogOpen(true);
  }, [navigateBackToProjectPanel]);

  React.useEffect(() => {
    if (loading || !editorIntroRequested) return;
    setEditorIntroOpen(true);
  }, [editorIntroRequested, loading]);

  React.useEffect(() => {
    if (loading || !effectiveProject) return;
    const analyticsKey = `${projectId}:${editorIntroSource}`;
    if (editorOpenTrackedRef.current === analyticsKey) return;
    editorOpenTrackedRef.current = analyticsKey;
    // Reuse the historical editor-open event so this new handoff remains comparable
    // with the existing activation funnel. No page content or field values are sent.
    activityApi.trackPanel({
      action: "landing_editor_open",
      projectId: String(effectiveProject?.id || effectiveProject?._id || ""),
      projectSlug: String(effectiveProject?.slug || ""),
      projectName: String(effectiveProject?.name || ""),
      path: location.pathname,
      journey: { channel: "panel", path: location.pathname, step: 0 },
      metadata: { analyticsOnly: true, telemetryVersion: 1, entrySource: editorIntroSource },
    }).catch(() => null);
  }, [editorIntroSource, effectiveProject, loading, location.pathname, projectId]);

  React.useEffect(() => {
    if (loading || !effectiveProject) return undefined;
    editorSessionStartedAtRef.current = Date.now();
    let sent = false;
    const sendSummary = () => {
      if (sent) return;
      sent = true;
      const dwellMs = Math.max(0, Date.now() - editorSessionStartedAtRef.current);
      activityApi.trackPanel({
        action: "onboarding_editor_session_summary",
        projectId: String(effectiveProject?.id || effectiveProject?._id || ""),
        projectSlug: String(effectiveProject?.slug || ""),
        projectName: String(effectiveProject?.name || ""),
        path: location.pathname,
        journey: { channel: "panel", path: location.pathname, durationMs: dwellMs },
        metadata: {
          analyticsOnly: true,
          telemetryVersion: 1,
          entrySource: editorIntroSource,
          guidedEntry: editorIntroRequested,
          dwellMs,
        },
      }).catch(() => null);
    };
    window.addEventListener("pagehide", sendSummary);
    return () => {
      window.removeEventListener("pagehide", sendSummary);
      sendSummary();
    };
  }, [editorIntroRequested, editorIntroSource, effectiveProject, loading, location.pathname, projectId]);

  const closeEditorIntro = React.useCallback(() => {
    setEditorIntroOpen(false);
    const params = new URLSearchParams(location.search);
    params.delete("intro");
    params.delete("source");
    const query = params.toString();
    navigate({ pathname: location.pathname, search: query ? `?${query}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const currentProjectId = String(projectId || "");
  const hasValidSettings = settingsLoadedProjectRef.current === currentProjectId;
  const settingsLoadError =
    settingsLoadFailure?.projectId === currentProjectId
      ? settingsLoadFailure.message
      : "";

  if (loading || (!hasValidSettings && !settingsLoadError)) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.3} alignItems="center">
          <WebProfitSpinner sx={{ color: BRAND.orange }} />
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
            Preparando la página editable…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (effectiveProject && !landingEnabled) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2, bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.5} sx={{ maxWidth: 520, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
            La página web no está habilitada
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,.62)" }}>
            Este proyecto todavía no tiene habilitado el módulo de página web.
          </Typography>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={leaveEditor} sx={{ alignSelf: "center" }}>
            Volver al panel
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!hasValidSettings && settingsLoadError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          p: { xs: 2, sm: 3 },
          bgcolor: "#F4F5F7",
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            maxWidth: 620,
            p: { xs: 2.25, sm: 3 },
            bgcolor: "#fff",
            borderRadius: 3,
            border: "1px solid rgba(25,36,47,.10)",
            boxShadow: "0 18px 48px rgba(25,36,47,.10)",
          }}
        >
          <Stack spacing={0.75}>
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
              No pudimos cargar tu página
            </Typography>
            <Typography sx={{ color: "rgba(25,36,47,.68)", lineHeight: 1.6 }}>
              El editor todavía no recibió la configuración real de este proyecto. No se aplicó ningún cambio ni se reemplazó tu contenido.
            </Typography>
          </Stack>

          <Alert severity="error" role="alert" sx={{ alignItems: "flex-start" }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.5 }}>
              {settingsLoadError}
            </Typography>
          </Alert>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ alignItems: { xs: "stretch", sm: "center" } }}
          >
            <Button
              variant="contained"
              onClick={retrySettingsLoad}
              disabled={settingsRetrying}
              startIcon={settingsRetrying ? <WebProfitSpinner size={18} color="inherit" /> : null}
              sx={{
                minWidth: 132,
                bgcolor: BRAND.orange,
                fontWeight: 900,
                "&:hover": { bgcolor: "#DF4E31" },
              }}
            >
              {settingsRetrying ? "Reintentando…" : "Reintentar"}
            </Button>
            <Button
              startIcon={<ArrowBackRoundedIcon />}
              onClick={leaveEditor}
              sx={{ fontWeight: 850 }}
            >
              Volver al panel
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-wp-tutorial-target="landing-editor" sx={{ minHeight: "100vh", bgcolor: "#F4F5F7" }}>
      <OnboardingTelemetryBridge />
      <DashboardTutorialEngine project={effectiveProject} autoStart={false} />
      <LandingEditorIntroCoach
        open={editorIntroOpen}
        project={effectiveProject}
        source={editorIntroSource}
        onClose={closeEditorIntro}
        onComplete={closeEditorIntro}
      />
      <LandingStudioToolbar
        title={form.business?.name || effectiveProject?.name || "Tu página web"}
        publicStatus={form.publicStatus}
        revision={form.revision}
        draftRevision={form.draftRevision}
        publishedRevision={form.publishedRevision}
        deployment={effectiveProject?.deployment}
        deploymentJob={deploymentJob}
        deploymentError={deploymentMonitorError}
        deploymentChecking={deploymentChecking}
        deploymentAction={deploymentAction}
        device={device}
        saving={saving}
        publishing={publishing}
        statusChanging={changingPublicStatus}
        previewing={previewing}
        openingPreview={openingPreview}
        hasUnsavedChanges={dirty}
        publicationValidation={publicationValidation}
        publicationValidationLoading={publicationValidationLoading}
        publicationValidationError={publicationValidationError}
        accessibilityIssueCount={accessibilityAudit.issues.length}
        canOpenPublic={Boolean(publicUrl)}
        onBack={leaveEditor}
        onChangeDevice={setDevice}
        onOpenSettings={(tab = 0) => openSettingsAt(tab, "global")}
        onOpenSections={() => openSettingsAt(2, "sections")}
        onSaveDraft={saveDraft}
        onTogglePreview={togglePreview}
        onOpenSecurePreview={openSecurePreview}
        onValidatePublication={validatePublication}
        onOpenAccessibility={openAccessibilityReview}
        onPublish={publishDraft}
        onOpenPublic={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}
        onRefreshDeployment={() => refreshDeploymentState()}
        onRetryDeployment={retryDeployment}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undoLastEdit}
        onRedo={redoLastEdit}
        onOpenHistory={openPublishedRevisions}
        onOpenGuide={() => {
          setEditorSurface("landing");
          setEditorIntroOpen(true);
        }}
        editorSurface={editorSurface}
        surfaceOptions={editorSurfaceOptions}
        onChangeSurface={changeEditorSurface}
        landingToolsAvailable={editorSurface === "landing"}
        publicationIntent={publicationIntent}
        gastronomyActionLabel={
          !productsEnabled || !gastronomicMenuEnabled
            ? "Usar Carta gastronómica"
            : gastronomicPresetInspection.changed
              ? "Preparar mi web para Carta"
              : "Revisar adaptación de Carta"
        }
        onGastronomyAction={requestGastronomicLandingAction}
        gastronomyActionBusy={gastronomicActionBusy}
      />

      {previewing ? (
        <Box
          component="main"
          sx={{
            position: "fixed",
            top: mobileViewport ? "var(--landing-studio-toolbar-height, 64px)" : "64px",
            bottom: 0,
            left: 0,
            right: 0,
            overflow: "auto",
            bgcolor: "#E9ECF0",
          }}
        >
          <Box
            sx={{
              minWidth: adaptiveMobilePreview ? "100%" : `${previewDevice.width + previewHorizontalGutter}px`,
              width: adaptiveMobilePreview ? "100%" : "auto",
              minHeight: "100%",
              py: 1.5,
              px: previewDevice.id === "mobile" ? 0 : 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                width: previewVisualWidth,
                maxWidth: adaptiveMobilePreview ? "100%" : "none",
                mb: 1,
                px: 0.25,
                color: "rgba(25,36,47,.64)",
                fontWeight: 900,
              }}
            >
              {previewDevice.label} · {adaptiveMobilePreview ? "ancho disponible" : `${previewDevice.width}px`} · vista limpia del borrador
            </Typography>
            <Box
              data-landing-preview-device={previewDevice.id}
              sx={{
                width: previewVisualWidth,
                maxWidth: adaptiveMobilePreview ? "100%" : "none",
                bgcolor: "#fff",
                borderRadius: previewDevice.id === "mobile" ? 2.5 : previewDevice.id === "tablet" ? 2 : 1.5,
                outline: "1px solid rgba(25,36,47,.12)",
                boxShadow: previewDevice.id === "desktop"
                  ? "0 14px 42px rgba(25,36,47,.12)"
                  : "0 18px 48px rgba(25,36,47,.18)",
                overflow: "hidden",
              }}
            >
              {editorSurface === "contact-page" ? (
                <ContactPageDraftPreview form={form} device={previewDevice.id} />
              ) : (
                <LandingCanvas
                  form={form}
                  device={previewDevice.id}
                  productsEnabled={productsEnabled}
                  gastronomicMenuEnabled={gastronomicMenuEnabled}
                  activeSection=""
                  studioMode={false}
                />
              )}
            </Box>
          </Box>
        </Box>
      ) : editorSurface === "mega-menu" ? (
        <MegaMenuEditor
          form={form}
          device={device}
          saving={saving}
          onCommit={commitPath}
        />
      ) : editorSurface === "products" ? (
        <ProductsPageEditor
          form={form}
          device={device}
          gastronomicMenuEnabled={gastronomicMenuEnabled}
          productsEnabled={productsEnabled}
          saving={saving}
          onCommit={commitPath}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
        />
      ) : editorSurface === "product" ? (
        <ProductPageEditor
          form={form}
          device={device}
          gastronomicMenuEnabled={gastronomicMenuEnabled}
          productsEnabled={productsEnabled}
          saving={saving}
          onCommit={commitPath}
        />
      ) : editorSurface === "contact-page" ? (
        <ContactPageEditor
          form={form}
          device={device}
          saving={saving}
          onCommit={commitPath}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
        />
      ) : editorSurface === "notifications" ? (
        <WebPushPromptEditor
          form={form}
          device={device}
          saving={saving}
          onCommit={commitPath}
        />
      ) : (
        <>
          <LandingStudioCanvasStage
            form={form}
            device={device}
            productsEnabled={productsEnabled}
            gastronomicMenuEnabled={gastronomicMenuEnabled}
            activeSection={activeSection}
            selectedElement={selectedElement}
            saving={saving}
            inspectorOpen={segmentInspectorOpen || elementInspectorOpen}
            questionDockVisible={!segmentInspectorOpen && !elementInspectorOpen}
            onSelectSection={selectCanvasSection}
            onSelectElement={selectCanvasElement}
            onCommit={commitPath}
            onCommitMany={commitMany}
            onUploadImage={uploadInlineImage}
            onRemoveImage={removeInlineImage}
            onOpenSegmentTool={openSegmentTool}
            onToggleSection={toggleCanvasSection}
            onRemoveSection={requestRemoveCanvasBlock}
          />

          {!segmentInspectorOpen && !elementInspectorOpen ? (
            <LandingStudioQuestionDock
              question={question}
              progress={progress}
              onResolve={() => runAction(question.action)}
              onSelectStep={selectProgressStep}
            />
          ) : null}
        </>
      )}

      {!previewing && editorSurface === "landing" ? (
        <LandingElementInspector
          open={elementInspectorOpen}
          selectedElement={selectedElement}
          shellSectionId={elementShellSectionId}
          form={form}
          baselineForm={savedDraftRef.current}
          saving={saving}
          onClose={() => {
            setElementInspectorOpen(false);
            setSelectedElement(null);
            setElementShellSectionId("");
          }}
          onCommit={commitPath}
          onToggleSection={toggleCanvasSection}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
          onOpenGlobalPalette={() => openSettingsAt(0, "global")}
        />
      ) : null}

      {!previewing && editorSurface === "landing" ? (
        <LandingSegmentInspector
          open={segmentInspectorOpen}
          sectionId={sectionId}
          initialTool={segmentInspectorTool}
          initialImagePath={segmentInspectorImagePath}
          form={form}
          baselineForm={savedDraftRef.current}
          productsEnabled={productsEnabled}
          saving={saving}
          onClose={() => setSegmentInspectorOpen(false)}
          onCommit={commitPath}
          onCommitMany={commitMany}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
        />
      ) : null}

      {!previewing ? (
        <LandingSettingsDialog
          open={settingsOpen}
          form={form}
          baselineForm={savedDraftRef.current}
          productsEnabled={productsEnabled}
          saving={savingPath === "settings"}
          changingPublicStatus={changingPublicStatus}
          initialTab={settingsInitialTab}
          mode={settingsMode}
          showTrialGuide={!progress.complete}
          onClose={() => setSettingsOpen(false)}
          onSave={saveSettingsDialog}
          onChangePublicStatus={applyPublicStatus}
          onUploadAsset={uploadAssetFile}
          onDeleteAsset={deferAssetDeletion}
        />
      ) : null}

      <Dialog
        open={Boolean(gastronomicDialogMode)}
        onClose={() => { if (!gastronomicActionBusy) setGastronomicDialogMode(""); }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="gastronomic-landing-dialog-title"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id="gastronomic-landing-dialog-title" sx={{ fontWeight: 950, color: BRAND.dark }}>
          {gastronomicDialogMode === "review" ? "Preparar mi web para Carta" : "Usar Carta gastronómica"}
        </DialogTitle>
        <DialogContent>
          {gastronomicDialogMode === "products-required" ? (
            <Alert severity="info">Carta necesita que la gestión de Productos/Ítems esté habilitada en este proyecto. No vamos a activar otros módulos automáticamente.</Alert>
          ) : null}
          {gastronomicDialogMode === "permission-required" ? (
            <Alert severity="warning">Para activar Carta desde el editor necesitás permiso para editar la configuración del proyecto. Pedile a un administrador que habilite Carta gastronómica.</Alert>
          ) : null}
          {gastronomicDialogMode === "activate" ? (
            <Stack spacing={1.5}>
              <Typography sx={{ color: "rgba(25,36,47,.74)", lineHeight: 1.6 }}>
                Activar Carta habilita la experiencia gastronómica del proyecto. Después vas a poder revisar por separado qué textos y bloques de la página web conviene adaptar.
              </Typography>
              <Alert severity="info">Activar Carta no modifica, guarda ni publica tu página web.</Alert>
            </Stack>
          ) : null}
          {gastronomicDialogMode === "review" ? (
            <Stack spacing={1.25}>
              <Typography sx={{ color: "rgba(25,36,47,.72)", lineHeight: 1.6 }}>
                WebProfit aplicará únicamente cambios seguros al borrador actual. Podés deshacer toda la adaptación con una sola acción.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.6, display: "grid", gap: 0.8, color: "rgba(25,36,47,.78)" }}>
                <li><strong>Textos:</strong> adapta sólo valores vacíos o defaults genéricos reconocidos.</li>
                <li><strong>Accesos:</strong> orienta los CTA default hacia la Carta y evita duplicar el acceso genérico de Productos.</li>
                <li><strong>Secciones:</strong> prioriza Carta, destacados, propuesta y contacto sólo si la estructura sigue siendo la inicial.</li>
                <li><strong>Conservado:</strong> textos personalizados, imágenes, colores, estilos, secciones propias y estructura trabajada.</li>
              </Box>
              {!gastronomicPresetInspection.structureDefault ? (
                <Alert severity="info">Detectamos que ya ordenaste, configuraste o agregaste secciones. Conservaremos esa estructura y sólo adaptaremos defaults seguros.</Alert>
              ) : null}
              {gastronomicCountLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" role="status" aria-live="polite">
                  <WebProfitSpinner size={18} />
                  <Typography variant="body2">Revisando contenido de tu Carta…</Typography>
                </Stack>
              ) : gastronomicMenuProductCount === 0 ? (
                <Alert severity="warning">Tu Carta todavía no tiene productos. Podés preparar la web ahora; los bloques de Carta mostrarán su estado vacío hasta que cargues contenido.</Alert>
              ) : null}
              {!gastronomicPresetInspection.changed ? (
                <Alert severity="success">Tu web ya está adaptada o no encontramos defaults seguros para modificar.</Alert>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setGastronomicDialogMode("")} disabled={gastronomicActionBusy} sx={{ fontWeight: 850 }}>
            {gastronomicDialogMode === "review" || gastronomicDialogMode === "activate" ? "Cancelar" : "Cerrar"}
          </Button>
          {gastronomicDialogMode === "activate" ? (
            <Button
              variant="contained"
              onClick={activateGastronomicMenu}
              disabled={gastronomicActionBusy}
              startIcon={gastronomicActionBusy ? <WebProfitSpinner size={18} color="inherit" /> : null}
              sx={{ bgcolor: BRAND.orange, fontWeight: 900, "&:hover": { bgcolor: "#DF4E31" } }}
            >
              {gastronomicActionBusy ? "Activando…" : "Activar Carta"}
            </Button>
          ) : null}
          {gastronomicDialogMode === "review" ? (
            <Button
              variant="contained"
              onClick={applyGastronomicLandingPreset}
              disabled={gastronomicActionBusy || !gastronomicPresetInspection.changed}
              sx={{ bgcolor: BRAND.orange, fontWeight: 900, "&:hover": { bgcolor: "#DF4E31" } }}
            >
              Aplicar adaptación
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingBlockRemoval)}
        onClose={blockRemovalBusy ? undefined : () => setPendingBlockRemovalId("")}
        aria-labelledby="remove-page-block-title"
        aria-describedby="remove-page-block-description"
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3.5 } }}
      >
        <DialogTitle id="remove-page-block-title" sx={{ fontWeight: 950, color: BRAND.dark }}>
          ¿Eliminar “{pendingBlockRemovalLabel}”?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.25}>
            <Typography id="remove-page-block-description" sx={{ color: "rgba(25,36,47,.72)", lineHeight: 1.6 }}>
              Este bloque se va a sacar de tu página web. No importa si lo creaste vos, Jarvis o lo armamos juntos: siempre podés editarlo o eliminarlo.
            </Typography>
            <Alert severity="info">
              La eliminación entra al historial del editor, así que podés deshacerla antes de guardar el borrador.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setPendingBlockRemovalId("")}
            disabled={blockRemovalBusy}
            sx={{ fontWeight: 850 }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRemoveCanvasBlock}
            disabled={blockRemovalBusy}
            startIcon={blockRemovalBusy ? <WebProfitSpinner size={18} color="inherit" /> : null}
            sx={{ fontWeight: 900 }}
          >
            {blockRemovalBusy ? "Eliminando…" : "Eliminar bloque"}
          </Button>
        </DialogActions>
      </Dialog>

      <LandingAccessibilityDrawer
        open={accessibilityOpen}
        audit={accessibilityAudit}
        onClose={() => setAccessibilityOpen(false)}
        onReviewIssue={reviewAccessibilityIssue}
      />

      <LandingRevisionsDrawer
        open={revisionsOpen}
        revisions={revisions}
        loading={revisionsLoading}
        error={revisionsError}
        sessionHistory={sessionHistory}
        sessionHistoryTotal={editHistoryRef.current.undo.length}
        canRedo={canRedo}
        restoringSiteRevision={restoringSiteRevision}
        currentPublishedRevision={form.publishedRevision}
        hasUnsavedChanges={dirty}
        onClose={() => setRevisionsOpen(false)}
        onReload={loadPublishedRevisions}
        onUndo={undoLastEdit}
        onRedo={redoLastEdit}
        onRestoreSessionCheckpoint={restoreSessionCheckpoint}
        onRestore={restorePublishedRevision}
      />

      <Dialog
        open={exitDialogOpen}
        onClose={cancelEditorExit}
        aria-labelledby="editor-unsaved-exit-title"
        aria-describedby="editor-unsaved-exit-description"
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id="editor-unsaved-exit-title" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Tenés cambios sin guardar
        </DialogTitle>
        <DialogContent>
          <Typography
            id="editor-unsaved-exit-description"
            sx={{ color: "rgba(25,36,47,.72)", lineHeight: 1.6 }}
          >
            Podés guardar el borrador antes de salir del editor o salir sin conservar estos últimos cambios. La versión publicada no se modifica por guardar el borrador.
          </Typography>
          {exitDialogError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2, alignItems: "flex-start" }}>
              {exitDialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            "& > :not(style) ~ :not(style)": { ml: { xs: 0, sm: 1 } },
          }}
        >
          <Button
            onClick={cancelEditorExit}
            disabled={exitDialogSaving || saving}
            sx={{ fontWeight: 850 }}
          >
            Cancelar
          </Button>
          <Button
            color="inherit"
            onClick={discardChangesAndLeave}
            disabled={exitDialogSaving || saving}
            sx={{ fontWeight: 850 }}
          >
            Salir sin guardar
          </Button>
          <Button
            variant="contained"
            onClick={saveAndLeaveEditor}
            disabled={exitDialogSaving || saving}
            startIcon={exitDialogSaving ? <WebProfitSpinner size={18} color="inherit" /> : null}
            sx={{
              bgcolor: BRAND.orange,
              fontWeight: 900,
              "&:hover": { bgcolor: "#DF4E31" },
            }}
          >
            {exitDialogSaving ? "Guardando…" : "Guardar y salir"}
          </Button>
        </DialogActions>
      </Dialog>

      {globalFeedback ? (
        <Snackbar
          key={globalFeedback.id}
          open
          autoHideDuration={globalFeedback.severity === "error" ? 5200 : 2200}
          onClose={() => setGlobalFeedback(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={globalFeedback.severity}
            variant="filled"
            onClose={() => setGlobalFeedback(null)}
            sx={{ fontWeight: 850 }}
          >
            {globalFeedback.message}
          </Alert>
        </Snackbar>
      ) : null}
    </Box>
  );
}
